// ============================================================================
// UsageLimitMeters — a member's own limit status (ORG-USAGE-2026)
//
// The expectation-setting surface. A member with limits always knows where
// they stand, long before anything stops them: the meter shows usage, the
// limit, the percentage, a plain-language reset time, and an explicit status
// label. Nobody should ever be surprised by a pause.
//
// Renders nothing when the member has no limits (the default) or is the owner.
// ============================================================================

import React from 'react';
import styled from 'styled-components';
import type { MyUsageLimit } from '../../services/rbacService';
import {
  ACTIVITY_COLORS,
  CARD_STYLE,
  formatResetTime,
  formatUsd,
  severityColor,
  severityLabel,
  SeverityLevel,
} from './activityTheme';

const Wrap = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: ${ACTIVITY_COLORS.ink};
  margin: 0 0 0.25rem;
  text-transform: lowercase;
`;

const SectionNote = styled.p`
  color: ${ACTIVITY_COLORS.inkSecondary};
  font-size: 0.8125rem;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const MeterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const MeterCard = styled.div`
  ${CARD_STYLE}
  padding: 1rem 1.125rem;
`;

const MeterHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
`;

const MeterName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${ACTIVITY_COLORS.ink};
  text-transform: lowercase;
`;

const MeterPct = styled.span<{ $color: string }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(p) => p.$color};
  font-variant-numeric: tabular-nums;
`;

/* Track + fill: 4px rounded data-end anchored to the start of the track. */
const Track = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${ACTIVITY_COLORS.gridline};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(p) => Math.max(p.$pct > 0 ? 2 : 0, Math.min(100, p.$pct))}%;
  background: ${(p) => p.$color};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const MeterFoot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  font-variant-numeric: tabular-nums;
`;

const StatusLabel = styled.span<{ $color: string; $emphasis: boolean }>`
  color: ${(p) => (p.$emphasis ? p.$color : ACTIVITY_COLORS.inkMuted)};
  font-weight: ${(p) => (p.$emphasis ? 600 : 400)};
`;

interface Props {
  limits: MyUsageLimit[];
  /** shown when an admin set limits but the plan makes them non-binding */
  enforcementNote?: string | null;
  compact?: boolean;
}

const UsageLimitMeters: React.FC<Props> = ({ limits, enforcementNote, compact }) => {
  if (!limits || limits.length === 0) return null;
  const nowMs = Date.now();

  return (
    <Wrap>
      {!compact && (
        <>
          <SectionTitle>your usage limits</SectionTitle>
          <SectionNote>
            set by your organization. you'll see a heads-up well before a limit is reached, and
            each one refreshes automatically.
            {enforcementNote ? ` ${enforcementNote}` : ''}
          </SectionNote>
        </>
      )}
      <MeterGrid>
        {limits.map((limit) => {
          const level = (limit.level ?? 'none') as SeverityLevel;
          const color = severityColor(level);
          const pct = Math.min(100, Math.round((limit.ratio ?? 0) * 100));
          const emphasize = level !== 'none';
          return (
            <MeterCard key={limit.id}>
              <MeterHead>
                <MeterName>{limit.label}</MeterName>
                <MeterPct $color={emphasize ? color : ACTIVITY_COLORS.ink}>{pct}%</MeterPct>
              </MeterHead>
              <Track
                role="meter"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${limit.label} usage: ${pct}% of limit used`}
              >
                <Fill $pct={pct} $color={color} />
              </Track>
              <MeterFoot>
                <span>
                  {/* $-based limits show dollars (dollars are not tokens);
                      token-metered limits render as % of the usage window —
                      raw token counts never display (2026-08-13 WU mandate).
                      Display only — enforcement still runs on the backend
                      metric untouched. */}
                  {limit.metric === 'usd'
                    ? `${formatUsd(limit.used)} of ${formatUsd(limit.limit)}`
                    : `${pct}% of usage window used`}
                </span>
                <span>refreshes {formatResetTime(limit.resetMs, nowMs)}</span>
              </MeterFoot>
              <MeterFoot>
                <StatusLabel $color={color} $emphasis={emphasize}>
                  {severityLabel(level)}
                </StatusLabel>
              </MeterFoot>
            </MeterCard>
          );
        })}
      </MeterGrid>
    </Wrap>
  );
};

export default UsageLimitMeters;
